import { type Appearance, type FormData } from '@fincode/js';

import { captureException } from '@/wabunka-src/common/utils';
/**
 * カード追加用のフォーム
 **/
interface FincodeCardModel {
  card_no: string; // カード番号
  expire: string; // 有効期限
  security_code: string; // CVC番号
  holder_name: string; // カード名義
}

/* eslint-disable @typescript-eslint/naming-convention */
export const buildDefaultFincodeModel = (result: FormData): FincodeCardModel => ({
  card_no: result.cardNo as string,
  expire: result.expire as string,
  security_code: result.CVC as string,
  holder_name: result.holderName as string,
});

/**
 * カード追加用のフォームのデフォルトの外観
 **/
export const createDefaultFincodeCardAppearance = () =>
  ({
    layout: 'vertical',
    hideLabel: false,
    hideHolderName: true,
    hidePayTimes: true,
    labelCardNo: 'Card number',
    labelExpire: 'Expiry date',
    labelCvc: 'CVC number',
    cardNo: '1234 ****',
    expireMonth: 'MM',
    expireYear: 'YY',
    cvc: 'CVC',
    holderName: 'TARO WABUNKA',
    colorBackground: 'fff',
    colorBackgroundInput: 'fff',
    colorText: '0f0f0f',
    colorPlaceHolder: 'grey',
    colorLabelText: '6e6e6e',
    colorBorder: 'e6e6e6',
    // fincodeの仕様で日本語のエラーメッセージが表示されてしまうため、背景と同色を指定することでユーザーの目に見えないようにしている
    colorError: 'ffffff',
    colorCheck: '000054',
    fontFamily: '"Noto Serif JP", serif',
  }) as Appearance;

export const validateFincodeCard = (result: FormData) => {
  const validationErrors: string[] = [];
  if (!result.cardNo) validationErrors.push('Card number is required.');
  if (!result.CVC) {
    validationErrors.push('CVC is required.');
  } else {
    // CVCが3桁か4桁の数字でない場合、エラー
    if (!/^\d{3,4}$/.test(result.CVC)) {
      validationErrors.push('CVC is invalid. Please enter a valid CVC.');
    }
  }
  if (!result.expire) {
    validationErrors.push('Card expiration date is required.');
  } else {
    // 有効期限が現在より前の場合はエラー
    // result.expireはYYMMの形式のstring
    // 現在日付とresult.expireを、YYYYMMの形式に変換して比較
    // expireYearはresult.expireの前2文字
    const expireYear = Number(`20${result.expire.slice(0, 2)}`);
    const expireMonth = Number(result.expire.slice(2));
    if (expireMonth > 12) {
      validationErrors.push('Card expiration month is invalid. Please enter a valid month.');
      return validationErrors;
    }
    const currentDate = new Date();
    // 次の月の0日目 = 今月の最終日
    // 入力された有効期限が過去の場合はエラー
    const expireDate = new Date(expireYear, expireMonth, 0);
    if (expireDate < currentDate) validationErrors.push('Card expiration date is invalid.');
  }
  return validationErrors;
};

export const FINCODE_ERROR_MESSAGES: Record<string, string> = {
  /**
   * カード番号
   */
  E0063103001: 'Card number is not specified.',
  E0063103008: 'The format of the card number is incorrect.',
  /**
   * 有効期限
   */
  E0063020001: 'Expiration date is not specified.',
  E0063020008: 'The format of the expiration date is incorrect.',
  /**
   * CVC
   */
  E0063100008: 'The format of the security code is incorrect.',
};

type FincodeErrorCode = keyof typeof FINCODE_ERROR_MESSAGES;

/**
 * fincodeから返ってくるエラーメッセージを英語に変換する
 */
export const convertToEnErrorMessages = (errorCode: string) => {
  // 型ガードを使用して、実際にキーが存在するか確認
  const isValidErrorCode = (code: string): code is FincodeErrorCode => {
    return code in FINCODE_ERROR_MESSAGES;
  };

  // 未定義のエラーコードが返ってきた場合は、エラーを捕捉してログ出力
  if (!isValidErrorCode(errorCode)) {
    captureException(new Error(`Undefined Fincode error code: ${errorCode}`));
    return 'An unexpected error occurred.';
  }

  return FINCODE_ERROR_MESSAGES[errorCode];
};
